import React, { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { Copy, RefreshCw } from "lucide-react";
import { useHd } from "../HelpdeskContext";
import { apiBase, crud, hd, errorMessage } from "../api";
import { useSettings } from "./GeneralSettings";
import { NumberField, SaveBar, Section, Toggle } from "./common";
import { T, TYPE } from "../../user/complaints/shared";

export default function ChatSettings() {
  const ctx = useHd();
  const { s, set, save, saving, dirty, reset } = useSettings();
  const [hours, setHours] = useState([]);
  const [key, setKey] = useState(null);
  useEffect(() => {
    crud("/business-hours").list().then(setHours).catch(() => {});
  }, []);
  if (!s) return <CircularProgress size={22} />;
  const c = s.chat;
  const widgetKey = key || c.widgetKey;
  const snippet = `<script src="${apiBase()}/public/helpdesk/widget.js" data-key="${widgetKey}" data-color="${c.color}" async></script>`;
  const rotate = async () => {
    try {
      const r = await hd.rotateWidgetKey();
      setKey(r.widgetKey);
      ctx.notify("Widget key rotated — update the snippet on your website");
    } catch (err) {
      ctx.notify(errorMessage(err), "error");
    }
  };
  const setC = (k) => set("chat", k);
  const setP = (k) => set("presence", k);
  return (
    <Box>
      <Section title="Website widget" description="Paste this snippet before </body> on any website. Customers chat with the AI assistant and your agents from there.">
        <Box sx={{ p: 1.5, bgcolor: "#101828", color: "#E4E7EC", borderRadius: "10px", fontFamily: "monospace", fontSize: "0.78rem", wordBreak: "break-all" }}>{snippet}</Box>
        <Button size="small" startIcon={<Copy size={14} />} sx={{ mt: 1 }} onClick={() => { navigator.clipboard?.writeText(snippet); ctx.notify("Snippet copied"); }}>Copy snippet</Button>
        <Button size="small" startIcon={<RefreshCw size={14} />} sx={{ mt: 1, ml: 1 }} onClick={rotate}>Rotate key</Button>
      </Section>
      <Section title="General">
        <Toggle label="Live chat enabled" checked={c.enabled} onChange={setC("enabled")} />
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Widget title" value={c.title} onChange={(e) => setC("title")(e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Brand colour" value={c.color} onChange={(e) => setC("color")(e.target.value)} /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" multiline label="Greeting" value={c.greeting} onChange={(e) => setC("greeting")(e.target.value)} /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" multiline label="AI disclosure (shown when the AI answers)" value={c.aiDisclosure} onChange={(e) => setC("aiDisclosure")(e.target.value)} /></Grid>
        </Grid>
        <Toggle label="Ask for email before chatting" checked={c.requireEmail} onChange={setC("requireEmail")} />
        <Toggle label="Ask for phone before chatting" checked={c.requirePhone} onChange={setC("requirePhone")} />
        <Toggle label="Show agent first names to customers" checked={c.showAgentName} onChange={setC("showAgentName")} />
      </Section>
      <Section title="Working hours & queue">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Chat working hours" value={c.businessHoursId || ""} onChange={(e) => setC("businessHoursId")(e.target.value || null)}>
              <MenuItem value="">Always available</MenuItem>{hours.map((h) => <MenuItem key={h._id} value={h._id}>{h.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Default team for chats" value={c.defaultTeamId || ""} onChange={(e) => setC("defaultTeamId")(e.target.value || null)}>
              <MenuItem value="">None</MenuItem>{ctx.teams.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}><TextField fullWidth size="small" multiline label="Offline message" value={c.offlineMessage} onChange={(e) => setC("offlineMessage")(e.target.value)} /></Grid>
          <Grid item xs={12}><TextField fullWidth size="small" multiline label="Queue message" value={c.queueMessage} onChange={(e) => setC("queueMessage")(e.target.value)} /></Grid>
          <Grid item><NumberField label="End inactive chats after (min)" value={c.autoEndInactiveMins} onChange={setC("autoEndInactiveMins")} helperText="0 = never" /></Grid>
        </Grid>
      </Section>
      <Section title="Allowed websites" description="Leave empty to allow any website. Otherwise list origins like https://www.example.com">
        <TextField fullWidth size="small" value={(c.allowedOrigins || []).join(", ")} onChange={(e) => setC("allowedOrigins")(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} />
      </Section>
      <Section title="Agent presence">
        <Toggle label="Set agents offline when they close the CRM" checked={s.presence.autoOfflineOnDisconnect} onChange={setP("autoOfflineOnDisconnect")} />
        <NumberField label="Mark idle agents away after (min)" value={s.presence.autoAwayAfterMins} onChange={setP("autoAwayAfterMins")} helperText="0 = never" />
      </Section>
      <Alert severity="info"><Typography sx={{ fontSize: TYPE.small, color: T.inkSecondary }}>AI chatbot behaviour and handoff rules are configured in <b>AI settings</b>.</Typography></Alert>
      <SaveBar dirty={dirty} saving={saving} onSave={() => save(["chat", "presence"])} onReset={reset} />
    </Box>
  );
}
